import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { mainSiteUrl } from '@/mocks/categories';

export default function Footer() {
  const { t, i18n } = useTranslation();

  const languages = [
    { code: 'en', label: 'EN' },
    { code: 'pl', label: 'PL' },
    { code: 'es', label: 'ES' },
    { code: 'fr', label: 'FR' },
  ];

  const changeLang = (code: string) => i18n.changeLanguage(code);

  const resources = [
    { label: t('nav.guides'), to: '/guides/usfans-spreadsheet-guide', internal: true },
    { label: t('module.review.title'), to: '/guides/usfans-spreadsheet-guide', internal: true },
    { label: t('module.shipping.title'), to: '/guides/usfans-shipping-cost', internal: true },
    { label: t('module.warehouse.title'), to: '/guides/usfans-warehouse', internal: true },
    { label: t('module.coupon.title'), to: '/guides/usfans-coupon', internal: true },
    { label: t('module.safety.title'), to: '/guides/is-usfans-legit', internal: true },
  ];

  const company = [
    { label: t('about.title'), to: '/#about', internal: true },
    { label: t('explore.title'), to: '/#explore', internal: true },
    { label: t('nav.categories'), to: '/#categories', internal: true },
  ];

  return (
    <footer className="bg-accent-500 text-foreground-950">
      <div className="w-full px-4 md:px-6 py-12 md:py-16">
        <div className="mx-auto max-w-7xl">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10">
            {/* Brand */}
            <div>
              <Link to="/" className="flex items-center gap-2">
                <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-50 text-primary-600">
                  <i className="ri-grid-line text-lg" />
                </span>
                <span className="font-heading font-semibold text-lg leading-none">USFans Spreadsheet</span>
              </Link>
              <p className="mt-4 text-sm leading-relaxed opacity-90">{t('footer.tagline')}</p>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="mt-5 inline-flex items-center gap-2 h-10 px-4 rounded-md bg-foreground-950 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-foreground-800 transition-colors"
              >
                {t('cta.secondary')}
                <i className="ri-arrow-right-up-line text-base" />
              </a>
            </div>

            {/* Resources */}
            <div>
              <h4 className="text-sm font-bold uppercase tracking-wider">{t('footer.resources')}</h4>
              <ul className="mt-4 space-y-2.5">
                {resources.map((item) => (
                  <li key={item.label}>
                    {item.internal ? (
                      <Link to={item.to} className="text-sm hover:underline whitespace-nowrap">
                        {item.label}
                      </Link>
                    ) : (
                      <a href={item.to} target="_blank" rel="noopener nofollow" className="text-sm hover:underline whitespace-nowrap">
                        {item.label}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            {/* Company */}
            <div>
              <h4 className="text-sm font-bold uppercase tracking-wider">{t('footer.company')}</h4>
              <ul className="mt-4 space-y-2.5">
                {company.map((item) => (
                  <li key={item.label}>
                    <a href={item.to} className="text-sm hover:underline whitespace-nowrap">
                      {item.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Languages */}
            <div>
              <h4 className="text-sm font-bold uppercase tracking-wider">{t('footer.languages')}</h4>
              <ul className="mt-4 flex flex-wrap gap-2">
                {languages.map((lang) => (
                  <li key={lang.code}>
                    <button
                      onClick={() => changeLang(lang.code)}
                      className="h-9 px-3 rounded-md bg-background-50/90 text-foreground-900 text-sm font-semibold whitespace-nowrap hover:bg-background-50 transition-colors"
                    >
                      {lang.label}
                    </button>
                  </li>
                ))}
              </ul>
              <p className="mt-5 text-xs leading-relaxed opacity-80">{t('footer.disclaimer')}</p>
            </div>
          </div>

          <div className="mt-10 pt-6 border-t border-foreground-950/15 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-xs opacity-80">© {new Date().getFullYear()} USFans Spreadsheet. All rights reserved.</p>
            <p className="text-xs opacity-80 text-center">Information may change over time. Always verify before making decisions.</p>
          </div>
        </div>
      </div>
    </footer>
  );
}